import { ClientLogger } from "@/lib/clientLogger";

// ─── Grouped State Types ────────────────────────────────────────────

export type AssistantOptionsState = {
  webSearch: boolean;
  extendedThinking: boolean;
  selectedMcpServers: string[];
  // When true the web search tool is restricted to the tenant-administrator-configured
  // recommended-sources allowlist (currently always empty until the admin panel ships —
  // see ADR-041). Tickbox off = unrestricted web search. Replaces the previous
  // user-editable domain list (webSearchDomains + webSearchDomainStrict, removed).
  webSearchLimitToRecommended: boolean;
  // Per-thread date filter per MCP server. Key: server name (e.g. "aspiCz");
  // value: ISO date string "YYYY-MM-DD". Only servers whose config has
  // `dateFilter: true` surface the UI; others ignore any stored entries.
  // Missing key = no filter for that server on this thread.
  mcpServerDateFilters: Record<string, string>;
};

const ASSISTANT_OPTIONS_DEFAULTS: AssistantOptionsState = {
  webSearch: false,
  extendedThinking: false,
  selectedMcpServers: [],
  webSearchLimitToRecommended: false,
  mcpServerDateFilters: {},
};

// ─── Key Registry ───────────────────────────────────────────────────

export const StorageKeys = {
  assistantOptions: "praetor:assistant:options",
  pdfPanelWidth: "praetor:pdf-panel-width",
  pdfZoom: "praetor:pdf-zoom",
  aiProvider: "praetor:assistant:aiProvider",
  lastViewedBranch: "praetor:lastLeaf",
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

// ─── SSR Guard ──────────────────────────────────────────────────────

function isClient(): boolean {
  return typeof window !== "undefined";
}

// ─── Thread-scoped Key Builder ──────────────────────────────────────

function threadKey(base: string, threadId: string): string {
  return `${base}:${threadId}`;
}

// ─── Raw String Operations ──────────────────────────────────────────

export function getRaw(key: string, fallback: string = ""): string {
  if (!isClient()) return fallback;
  return localStorage.getItem(key) ?? fallback;
}

export function setRaw(key: string, value: string): void {
  if (!isClient()) return;
  localStorage.setItem(key, value);
}

function removeKey(key: string): void {
  if (!isClient()) return;
  localStorage.removeItem(key);
}

// ─── JSON Operations ────────────────────────────────────────────────

function getJson<T>(key: string, fallback: T): T {
  if (!isClient()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch (e) {
    ClientLogger.debug("Storage JSON parse failed", { error: e, action: key });
    return fallback;
  }
}

function setJson<T>(key: string, value: T): void {
  if (!isClient()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    ClientLogger.debug("Storage write failed", { error: e, action: key });
  }
}

// ─── Assistant Options (grouped per-thread state) ───────────────────

export function getAssistantOptions(
  threadId: string | undefined,
): AssistantOptionsState {
  if (!threadId) return { ...ASSISTANT_OPTIONS_DEFAULTS };
  const stored = getJson<Partial<AssistantOptionsState>>(
    threadKey(StorageKeys.assistantOptions, threadId),
    {},
  );
  return { ...ASSISTANT_OPTIONS_DEFAULTS, ...stored };
}

export function updateAssistantOptions(
  threadId: string | undefined,
  partial: Partial<AssistantOptionsState>,
): void {
  if (!threadId) return;
  const key = threadKey(StorageKeys.assistantOptions, threadId);
  const current = getJson<Partial<AssistantOptionsState>>(key, {});
  setJson(key, { ...current, ...partial });
}

// ─── Last-viewed branch leaf (per thread) ───────────────────────────
// Cached client-side so a cold load can restore the user's branch instantly,
// before /threads/{id}/messages comes back with the server-canonical pointer.
// Server is still the source of truth — on each load() we reconcile the cache
// against the envelope's lastViewedMessageId (last write wins).

export function getLastViewedBranch(threadId: string): string | null {
  return getRaw(threadKey(StorageKeys.lastViewedBranch, threadId)) || null;
}

export function setLastViewedBranch(threadId: string, leafId: string | null): void {
  const key = threadKey(StorageKeys.lastViewedBranch, threadId);
  if (leafId === null) {
    removeKey(key);
    return;
  }
  setRaw(key, leafId);
}

// ─── Global AI Provider ─────────────────────────────────────────────

export function getAiProvider(): string | null {
  return getRaw(StorageKeys.aiProvider) || null;
}

export function setAiProvider(name: string): void {
  setRaw(StorageKeys.aiProvider, name);
}

// ─── PDF Zoom preference ─────────────────────────────────────────────
// Stores the last explicit zoom mode/value chosen by the user.
// Value is either a named mode ("page-fit", "page-width") or a numeric
// string ("0.85"). Null means no preference — callers fall back to "page-fit".

export function getPdfZoom(): string | null {
  return getRaw(StorageKeys.pdfZoom) || null;
}

export function setPdfZoom(value: string): void {
  setRaw(StorageKeys.pdfZoom, value);
}
